import { memo, useCallback, useMemo } from 'react';

import {
  ChakraSimpleGrid,
  FAIconName,
  InputGroupV2,
  RadioV2,
  TextInputV2,
  Tooltip,
  UniversalSelect,
  convertDashedUSPhoneNumberToInternationalFormat,
} from '@equip.health/ui';
import { validate as validateEmail } from 'email-validator';

import {
  InquiryInfoValue,
  PatientInfoValue,
} from '~/components/common/inquiry/drawer/types';
import {
  FieldLength,
  TextInputV2Type,
} from '~/lib/constants/inquiry.constants';
import {
  dropdownMenuOptionsMapper,
  getSelectedUniversalSelectOption,
} from '~/lib/util/inquiry.util';
import { getAgeFromDOB } from '~/lib/util/patient.util';
import util from '~/lib/util/util';

import InquiryDrawerLayout from './InquiryDrawerLayout';
import LanguageDropdown from './LanguageDropdown';
import PronounsDropdown from './PronounsDropdown';
import StateDropdown from './StateDropdown';
import TimezoneDropdown from './TimezoneDropdown';
import { useGetGendersEnumQuery } from '~/store/services/enumerations';
import { ConsentToRecord } from '~/lib/constants/patients.constants';
import { getConsentText, getConsentToRecordOptions } from '../../../maud/util';
const TOP_LEVEL_ID = 'patient-info';

interface PatientInfoProps {
  patientInfoValue: PatientInfoValue;
  patientAge: number;
  showRequiredFieldErrors: boolean;
  updateInfo: (info: Partial<InquiryInfoValue>) => void;
  updatePatientInfo: (info: Partial<PatientInfoValue>) => void;
}

const PatientInfo = memo(
  ({
    patientInfoValue,
    patientAge,
    showRequiredFieldErrors,
    updateInfo,
    updatePatientInfo,
  }: PatientInfoProps) => {
    const { data: genders } = useGetGendersEnumQuery();
    const {
      chosenName,
      dateOfBirth,
      email,
      firstName,
      genderOnRecord,
      hasGivenRecordingConsent,
      isPrimaryContact,
      hasAppAccess,
      languages,
      lastName,
      phoneNumber,
      pronounsOnRecord,
      state,
      timezone,
    } = patientInfoValue;
    const age = getAgeFromDOB(dateOfBirth) ?? patientAge;
    const dobYear =
      new Date(Date.now()).getFullYear() - new Date(dateOfBirth).getFullYear();
    const isEmailValid = useMemo<boolean>(() => validateEmail(email), [email]);
    const isPhoneValid = useMemo<boolean>(
      () =>
        util.validatePhoneNumber(
          convertDashedUSPhoneNumberToInternationalFormat(phoneNumber ?? ''),
        ),
      [phoneNumber],
    );
    const isEmailRequired = isPrimaryContact && !isPhoneValid;
    const isPhoneRequired = isPrimaryContact && !isEmailValid;
    const showIsInvalidEmail =
      (showRequiredFieldErrors && isEmailRequired && !isEmailValid) ||
      (email && !isEmailValid);
    const showIsInvalidPhone =
      (showRequiredFieldErrors && isPhoneRequired && !isPhoneValid) ||
      (phoneNumber && !isPhoneValid);
    const showIsInvalidDateOfBirth =
      (dateOfBirth && dateOfBirth.length < FieldLength.date) || dobYear < 0;

    const isAgeDisabled =
      dateOfBirth?.length === FieldLength.date && dobYear >= 0;

    const invalidFirstName = showRequiredFieldErrors && !firstName?.trim();
    const invalidLastName = showRequiredFieldErrors && !lastName?.trim();
    const invalidState = showRequiredFieldErrors && !state;
    const invalidLanguage = showRequiredFieldErrors && !languages?.[0];

    const isMissingFields =
      showIsInvalidEmail ||
      showIsInvalidPhone ||
      showIsInvalidDateOfBirth ||
      invalidFirstName ||
      invalidLastName ||
      invalidState ||
      invalidLanguage;

    const genderOptions = useMemo<UniversalSelectOption[]>(() => {
      return dropdownMenuOptionsMapper((genders || []).map((g) => g.label));
    }, [genders]);

    const updateStringValue = useCallback(
      (key: keyof PatientInfoValue) =>
        (val: string): void => {
          updatePatientInfo({ [key]: val });
        },
      [],
    );

    const updateArrayValue = useCallback(
      (key: keyof PatientInfoValue) =>
        (val: string): void => {
          updatePatientInfo({ [key]: val ? [val] : null });
        },
      [],
    );

    return (
      <InquiryDrawerLayout
        header="Patient information"
        id={TOP_LEVEL_ID}
        isMissingFields={isMissingFields}
      >
        <ChakraSimpleGrid columns={3} spacing="24px" width="100%">
          <InputGroupV2 id={`${TOP_LEVEL_ID}__first-name`} label="First name">
            <TextInputV2
              id={`${TOP_LEVEL_ID}__first-name`}
              isInvalid={invalidFirstName}
              onChange={updateStringValue('firstName')}
              value={firstName}
            />
          </InputGroupV2>
          <InputGroupV2 id={`${TOP_LEVEL_ID}__last-name`} label="Last name">
            <TextInputV2
              id={`${TOP_LEVEL_ID}__last-name`}
              isInvalid={invalidLastName}
              onChange={updateStringValue('lastName')}
              value={lastName}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__chosen-name`}
            isRequired={false}
            label="Chosen name"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__chosen-name`}
              onChange={updateStringValue('chosenName')}
              value={chosenName}
            />
          </InputGroupV2>
        </ChakraSimpleGrid>
        <ChakraSimpleGrid columns={2} spacing="24px" width="100%">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__phone-number`}
            isRequired={isPhoneRequired}
            label="Phone number"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__phone-number`}
              isInvalid={showIsInvalidPhone}
              leftIcon={FAIconName.mobileRegular}
              onChange={(value: string) =>
                updatePatientInfo({
                  isInvalidPhone: !util.validatePhoneNumber(
                    convertDashedUSPhoneNumberToInternationalFormat(
                      value ?? '',
                    ),
                  ),
                  phoneNumber: value,
                })
              }
              type={TextInputV2Type.PHONE}
              value={phoneNumber}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__email-address`}
            isRequired={isEmailRequired}
            label="Email address"
          >
            <Tooltip
              id={`${TOP_LEVEL_ID}__patient-email-tooltip`}
              isDisabled={!hasAppAccess}
              placement="top"
              tooltipText="This patient has been granted app access. To edit this email address, please go to the patient profile."
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__email-address`}
                isDisabled={hasAppAccess}
                isInvalid={showIsInvalidEmail}
                leftIcon={FAIconName.envelopeRegular}
                onChange={(value: string) =>
                  updatePatientInfo({
                    email: value,
                    isInvalidEmail: !validateEmail(value),
                  })
                }
                type={TextInputV2Type.EMAIL}
                value={email}
              />
            </Tooltip>
          </InputGroupV2>
          <StateDropdown
            id={TOP_LEVEL_ID}
            isInvalid={invalidState}
            isRequired
            label="Current State"
            onSelect={updateStringValue('state')}
            value={state}
          />
          <TimezoneDropdown
            id={TOP_LEVEL_ID}
            onSelect={updateStringValue('timezone')}
            value={timezone}
          />
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__dob`}
            isRequired={false}
            label="Date of birth"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__dob`}
              isInvalid={showIsInvalidDateOfBirth}
              onChange={(value: string) => {
                updatePatientInfo({
                  dateOfBirth: value,
                });
                if (value.length === FieldLength.date && dobYear >= 0) {
                  updateInfo({
                    patientAge: getAgeFromDOB(value),
                  });
                }
              }}
              placeholder="MM/DD/YYYY"
              type={TextInputV2Type.DATE}
              value={dateOfBirth}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__age`}
            isRequired={false}
            label="Age"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__age`}
              isDisabled={isAgeDisabled}
              onChange={(value: string) =>
                updateInfo({
                  patientAge: parseInt(value, 10),
                })
              }
              type={TextInputV2Type.NUMBER}
              value={age?.toString()}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__gender`}
            isRequired={false}
            label="Gender on record"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__gender`}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updatePatientInfo({
                  genderOnRecord: selected?.value,
                })
              }
              options={genderOptions}
              value={getSelectedUniversalSelectOption(
                genderOnRecord,
                genderOptions,
              )}
            />
          </InputGroupV2>
          <PronounsDropdown
            id={TOP_LEVEL_ID}
            onSelectValues={(values) =>
              updatePatientInfo({
                pronounsOnRecord: values ? [...values] : null,
              })
            }
            values={pronounsOnRecord}
          />
          <LanguageDropdown
            id={TOP_LEVEL_ID}
            isInvalid={invalidLanguage}
            isRequired
            onSelect={updateArrayValue('languages')}
            value={languages?.[0]}
          />
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__patient-consent-to-record`}
            label="Consent to record"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__patient-consent-to-record-select`}
              isClearable={false}
              isSingleSelect
              onChange={(options) => {
                const consentGiven =
                  options.value == ConsentToRecord.ConsentGiven;
                updatePatientInfo({
                  hasGivenRecordingConsent: consentGiven,
                });
              }}
              options={getConsentToRecordOptions(TOP_LEVEL_ID)}
              placeholder="Please choose"
              value={getSelectedUniversalSelectOption(
                getConsentText(hasGivenRecordingConsent),
                getConsentToRecordOptions(TOP_LEVEL_ID),
              )}
            />
          </InputGroupV2>
        </ChakraSimpleGrid>
        <RadioV2
          id={`${TOP_LEVEL_ID}__primary-contact`}
          isChecked={isPrimaryContact}
          label="Patient is primary contact"
          onChange={(val: boolean) =>
            updatePatientInfo({
              isPrimaryContact: val,
            })
          }
        />
      </InquiryDrawerLayout>
    );
  },
);

export default PatientInfo;
